import apiClient from './apiClient';

// =====================================================
// LOCATION MANAGEMENT
// =====================================================

export const createLocation = async (data) => {
  const response = await apiClient.post('/greige-storage/admin/locations', data);
  return response.data;
};

export const getLocations = async (level = null, hallName = null) => {
  const params = {};
  if (level) params.level = level;
  if (hallName) params.hall_name = hallName;
  const response = await apiClient.get('/greige-storage/admin/locations', { params });
  return response.data;
};

export const getLocation = async (locationId) => {
  const response = await apiClient.get(`/greige-storage/admin/locations/${locationId}`);
  return response.data;
};

export const updateLocation = async (locationId, data) => {
  const response = await apiClient.put(`/greige-storage/admin/locations/${locationId}`, data);
  return response.data;
};

export const deleteLocation = async (locationId) => {
  const response = await apiClient.delete(`/greige-storage/admin/locations/${locationId}`);
  return response.data;
};

// ── Greige Location Locks (bin-wise lock/unlock, mirrors SFG/FG) ──
export const getLocationLocks = async () => {
  const response = await apiClient.get('/greige-storage/location-locks');
  return response.data;
};

export const getLocationLocksSummary = async () => {
  const response = await apiClient.get('/greige-storage/location-locks/summary');
  return response.data;
};

export const setLocationLock = async (payload) => {
  const response = await apiClient.post('/greige-storage/location-locks/set', payload);
  return response.data;
};

export const resyncLocationLockStatus = async () => {
  const response = await apiClient.post('/greige-storage/location-locks/resync-inventory-status');
  return response.data;
};


export const getHalls = async () => {
  const response = await apiClient.get('/greige-storage/admin/halls');
  return response.data;
};

export const getFloors = async (hallName) => {
  const response = await apiClient.get(`/greige-storage/admin/floors/${encodeURIComponent(hallName)}`);
  return response.data;
};

export const getRows = async (hallName, floorName) => {
  const response = await apiClient.get(`/greige-storage/admin/rows/${encodeURIComponent(hallName)}/${encodeURIComponent(floorName)}`);
  return response.data;
};

export const getPositions = async (hallName, floorName, rowName) => {
  const response = await apiClient.get(`/greige-storage/admin/positions/${encodeURIComponent(hallName)}/${encodeURIComponent(floorName)}/${encodeURIComponent(rowName)}`);
  return response.data;
};

// =====================================================
// INCOMING CONSIGNMENTS
// =====================================================

export const getIncomingConsignments = async () => {
  const response = await apiClient.get('/greige-storage/incoming-consignments');
  return response.data;
};

export const requestConsignment = async (entryId) => {
  const response = await apiClient.post(`/greige-storage/incoming-consignments/${encodeURIComponent(entryId)}/request`);
  return response.data;
};

export const acceptConsignment = async (entryId) => {
  const response = await apiClient.post(`/greige-storage/incoming-consignments/${encodeURIComponent(entryId)}/accept`);
  return response.data;
};

export const rejectConsignment = async (entryId, reason) => {
  const response = await apiClient.post(`/greige-storage/incoming-consignments/${encodeURIComponent(entryId)}/reject`, null, {
    params: { reason }
  });
  return response.data;
};

// =====================================================
// BUNDLE RECEIPT
// =====================================================

export const getDocumentsForReceipt = async () => {
  const response = await apiClient.get('/greige-storage/bundle-receipt/documents');
  return response.data;
};

export const startBundleReceipt = async (entryId) => {
  const response = await apiClient.post(`/greige-storage/bundle-receipt/start/${entryId}`);
  return response.data;
};

export const scanBundleForReceipt = async (entryId, bundleBarcode) => {
  const response = await apiClient.post(`/greige-storage/bundle-receipt/scan/${entryId}`, null, {
    params: { bundle_barcode: bundleBarcode }
  });
  return response.data;
};

export const approveBundleUsnCount = async (entryId, bundleBarcode) => {
  const response = await apiClient.post(`/greige-storage/bundle-receipt/approve-bundle/${entryId}/${encodeURIComponent(bundleBarcode)}`);
  return response.data;
};

export const startQtyMismatch = async (entryId, bundleBarcode) => {
  const response = await apiClient.post(`/greige-storage/bundle-receipt/qty-mismatch/${entryId}/${encodeURIComponent(bundleBarcode)}`);
  return response.data;
};

export const scanUsnForMismatch = async (entryId, bundleBarcode, usnCode) => {
  const response = await apiClient.post(`/greige-storage/bundle-receipt/scan-usn/${entryId}/${encodeURIComponent(bundleBarcode)}`, null, {
    params: { usn_code: usnCode }
  });
  return response.data;
};

export const completeMismatchVerification = async (entryId, bundleBarcode) => {
  const response = await apiClient.post(`/greige-storage/bundle-receipt/complete-mismatch/${entryId}/${encodeURIComponent(bundleBarcode)}`);
  return response.data;
};

export const getMismatchReports = async (status = null) => {
  const params = {};
  if (status) params.status = status;
  const response = await apiClient.get('/greige-storage/mismatch-reports', { params });
  return response.data;
};

export const resolveMismatchReport = async (reportId, resolutionNotes) => {
  const response = await apiClient.post(`/greige-storage/mismatch-reports/${reportId}/resolve`, null, {
    params: { resolution_notes: resolutionNotes }
  });
  return response.data;
};

export const completeBundleReceipt = async (entryId) => {
  const response = await apiClient.post(`/greige-storage/bundle-receipt/complete/${entryId}`);
  return response.data;
};

// =====================================================
// LOCATION PUT AWAY
// =====================================================

export const getBundlesPendingPutAway = async () => {
  const response = await apiClient.get('/greige-storage/put-away/bundles-pending');
  return response.data;
};

export const suggestPutAwayLocation = async (bundleBarcode) => {
  const response = await apiClient.get(`/greige-storage/put-away/suggest/${encodeURIComponent(bundleBarcode)}`);
  return response.data;
};

export const putAwayBundle = async (staffId, bundleBarcode, locationId, opts = {}) => {
  const response = await apiClient.post('/greige-storage/put-away', {
    staff_id: staffId,
    bundle_barcode: bundleBarcode,
    location_id: locationId,
    overridden: !!opts.overridden,
    override_reason: opts.overrideReason || null,
    staff_ids: opts.staffIds || null,
    staff_index: opts.staffIndex || 0,
  });
  return response.data;
};

export const validateGreigeLocation = async (code, expectedDesign = null, purpose = null) => {
  const params = { code };
  if (expectedDesign) params.expected_design = expectedDesign;
  if (purpose) params.purpose = purpose;
  const response = await apiClient.get('/greige-storage/put-away/validate-location', { params });
  return response.data;
};

export const markGreigeBinFull = async (locationId, bundleBarcode = null, designName = null) => {
  const response = await apiClient.post('/greige-storage/put-away/bin-full', {
    location_id: locationId,
    bundle_barcode: bundleBarcode,
    design_name: designName,
  });
  return response.data;
};

export const getAllPositions = async () => {
  const response = await apiClient.get('/greige-storage/admin/locations', { params: { level: 'position' } });
  return response.data;
};

export const getGreigeAgingReport = async (designName = null) => {
  const params = {};
  if (designName) params.design_name = designName;
  const response = await apiClient.get('/greige-storage/reports/aging', { params });
  return response.data;
};

// =====================================================
// LOCATION TO LOCATION TRANSFER
// =====================================================

export const locationTransfer = async (staffId, destinationLocationId, bundleBarcodes) => {
  const response = await apiClient.post('/greige-storage/location-transfer', {
    staff_id: staffId,
    destination_location_id: destinationLocationId,
    bundle_barcodes: bundleBarcodes
  });
  return response.data;
};

export const getStoredBundle = async (bundleBarcode) => {
  const response = await apiClient.get('/greige-storage/stored-bundle', {
    params: { bundle_barcode: bundleBarcode }
  });
  return response.data;
};

// =====================================================
// SHIPMENT REQUESTS
// =====================================================

export const getShipmentRequests = async (status = null) => {
  const params = {};
  if (status) params.status = status;
  const response = await apiClient.get('/greige-storage/shipment-requests', { params });
  return response.data;
};

export const createShipmentRequest = async (data) => {
  const response = await apiClient.post('/greige-storage/shipment-requests', data);
  return response.data;
};

export const acceptShipmentRequest = async (requestId) => {
  const response = await apiClient.post(`/greige-storage/shipment-requests/${requestId}/accept`);
  return response.data;
};

export const rejectShipmentRequest = async (requestId, reason) => {
  const response = await apiClient.post(`/greige-storage/shipment-requests/${requestId}/reject`, null, {
    params: { reason }
  });
  return response.data;
};

export const acceptShipmentRequestGroup = async (categoryRequestId) => {
  const response = await apiClient.post(`/greige-storage/shipment-requests/group/${categoryRequestId}/accept`);
  return response.data;
};

export const rejectShipmentRequestGroup = async (categoryRequestId, reason) => {
  const response = await apiClient.post(`/greige-storage/shipment-requests/group/${categoryRequestId}/reject`, null, {
    params: { reason }
  });
  return response.data;
};

// =====================================================
// PICK SLIPS
// =====================================================

export const generatePickSlip = async (requestId) => {
  const response = await apiClient.post(`/greige-storage/pick-slip/generate/${requestId}`);
  return response.data;
};

export const getPickSlips = async (status = null) => {
  const params = {};
  if (status) params.status = status;
  const response = await apiClient.get('/greige-storage/pick-slips', { params });
  return response.data;
};

// =====================================================
// PICKING
// =====================================================

export const assignPicker = async (pickSlipId, pickerStaffId) => {
  const response = await apiClient.post(`/greige-storage/picking/assign/${pickSlipId}`, null, {
    params: { picker_staff_id: pickerStaffId }
  });
  return response.data;
};

export const pickBundle = async (pickSlipId, bundleBarcode) => {
  const response = await apiClient.post(`/greige-storage/picking/pick/${pickSlipId}/${encodeURIComponent(bundleBarcode)}`);
  return response.data;
};

// =====================================================
// ASSORTMENT PICKING (buried-code, colour-ratio)
// =====================================================

export const getAssortmentDesigns = async (search = null) => {
  const params = {};
  if (search) params.search = search;
  const response = await apiClient.get('/greige-storage/assortment/designs', { params });
  return response.data;
};

export const previewAssortment = async (designName, targetQty, pool = null) => {
  const response = await apiClient.post('/greige-storage/assortment/preview', {
    design_name: designName,
    target_qty: targetQty,
    ...(pool ? { pool } : {}),
  });
  return response.data;
};

export const generateAssortment = async (payload) => {
  const response = await apiClient.post('/greige-storage/assortment/generate', payload);
  return response.data;
};

export const approveAssortmentSlip = async (pickSlipId, reason = '') => {
  const response = await apiClient.post(`/greige-storage/assortment/pick-slips/${pickSlipId}/approve`, { reason });
  return response.data;
};

export const rejectAssortmentSlip = async (pickSlipId, reason = '') => {
  const response = await apiClient.post(`/greige-storage/assortment/pick-slips/${pickSlipId}/reject`, { reason });
  return response.data;
};

export const getAssortmentPickSlips = async (status = null) => {
  const params = {};
  if (status) params.status = status;
  const response = await apiClient.get('/greige-storage/assortment/pick-slips', { params });
  return response.data;
};

export const deleteAssortmentSlip = async (pickSlipId) => {
  const response = await apiClient.delete(`/greige-storage/assortment/pick-slips/${pickSlipId}`);
  return response.data;
};

export const getAssortmentPickSlip = async (pickSlipId) => {
  const response = await apiClient.get(`/greige-storage/assortment/pick-slips/${pickSlipId}`);
  return response.data;
};

export const assignAssortmentPicker = async (pickSlipId, pickerStaffId) => {
  const response = await apiClient.post(`/greige-storage/assortment/pick-slips/${pickSlipId}/assign`, {
    picker_staff_id: pickerStaffId,
  });
  return response.data;
};

export const pickAssortmentLine = async (pickSlipId, lineId, actualPieces, staffId = null) => {
  const response = await apiClient.post(`/greige-storage/assortment/pick-slips/${pickSlipId}/pick-line`, {
    line_id: lineId,
    actual_pieces: actualPieces,
    staff_id: staffId,
  });
  return response.data;
};

export const scanAssortmentBundle = async (pickSlipId, bundleBarcode, staffId = null) => {
  const response = await apiClient.post(`/greige-storage/assortment/pick-slips/${pickSlipId}/scan-bundle`, {
    bundle_barcode: bundleBarcode,
    staff_id: staffId,
  });
  return response.data;
};

export const completeAssortmentSlip = async (pickSlipId) => {
  const response = await apiClient.post(`/greige-storage/assortment/pick-slips/${pickSlipId}/complete`);
  return response.data;
};

export const setAssortmentPool = async (pickSlipId, pool) => {
  const response = await apiClient.post(`/greige-storage/assortment/pick-slips/${pickSlipId}/set-pool`, { pool });
  return response.data;
};

// ---- Assortment Target-Design conversion (parent/child sort nos) ----
export const getAssortmentTargetDesigns = async (sourceDesign) => {
  const response = await apiClient.get('/greige-storage/assortment/target-designs', {
    params: { source_design: sourceDesign },
  });
  return response.data;
};

export const getAssortmentConversionPreview = async (pickSlipId) => {
  const response = await apiClient.get(`/greige-storage/assortment/pick-slips/${pickSlipId}/conversion-preview`);
  return response.data;
};

export const convertAssortmentSlip = async (pickSlipId, staffId = null) => {
  const response = await apiClient.post(`/greige-storage/assortment/pick-slips/${pickSlipId}/convert`, {
    staff_id: staffId,
  });
  return response.data;
};

export const getAvailableGreigePickers = async () => {
  const response = await apiClient.get('/greige-storage/assortment/available-pickers');
  return response.data;
};

export const setAvailableGreigePickers = async (staffIds) => {
  const response = await apiClient.post('/greige-storage/assortment/available-pickers', {
    staff_ids: staffIds,
  });
  return response.data;
};

export const getPendingRelocations = async () => {
  const response = await apiClient.get('/greige-storage/relocations/pending');
  return response.data;
};

export const confirmRelocation = async (bundleBarcode, locationId, staffId) => {
  const response = await apiClient.post('/greige-storage/relocations/confirm', {
    bundle_barcode: bundleBarcode,
    location_id: locationId,
    staff_id: staffId,
  });
  return response.data;
};

// =====================================================
// SORT NO TRANSFER
// =====================================================

export const sortNoTransfer = async (staffId, bundleBarcodes, newSortNo) => {
  const response = await apiClient.post('/greige-storage/sort-transfer', {
    staff_id: staffId,
    bundle_barcodes: bundleBarcodes,
    new_sort_no: newSortNo
  });
  return response.data;
};

// =====================================================
// INTERNAL TRANSFER
// =====================================================

export const getAvailableSections = async () => {
  const response = await apiClient.get('/greige-storage/internal-transfer/sections');
  return response.data;
};

export const internalTransfer = async (staffId, targetSection, bundleBarcodes, remarks = null) => {
  const response = await apiClient.post('/greige-storage/internal-transfer', null, {
    params: {
      staff_id: staffId,
      target_section: targetSection,
      bundle_barcodes: bundleBarcodes,
      remarks
    }
  });
  return response.data;
};

// =====================================================
// REPORTS
// =====================================================

export const getDailySummaryReport = async (startDate = null, endDate = null) => {
  const params = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  const response = await apiClient.get('/greige-storage/reports/daily-summary', { params });
  return response.data;
};

export const getDrillDownData = async (date, type) => {
  const response = await apiClient.get(`/greige-storage/reports/drill-down/${date}/${type}`);
  return response.data;
};

export const getInventorySummary = async () => {
  const response = await apiClient.get('/greige-storage/reports/inventory-summary');
  return response.data;
};

// Enhanced Reports
export const getAvailableOperations = async () => {
  const response = await apiClient.get('/greige-storage/reports/operations');
  return response.data;
};

export const getEnhancedSummaryReport = async (params = {}) => {
  const response = await apiClient.get('/greige-storage/reports/enhanced-summary', { params });
  return response.data;
};

export const getEnhancedTransactions = async (params = {}) => {
  const response = await apiClient.get('/greige-storage/reports/enhanced-transactions', { params });
  return response.data;
};

export const getClosingStockReport = async (params = {}) => {
  const response = await apiClient.get('/greige-storage/reports/closing-stock', { params });
  return response.data;
};

// Location-wise stock report (STORED bundles only) — mirrors SFG/FG Inventory Report
export const getLocationStockReport = async (params = {}) => {
  const response = await apiClient.get('/greige-storage/location-stock-report', { params });
  return response.data;
};

export const getInternalTransferReport = async (params = {}) => {
  const response = await apiClient.get('/greige-storage/internal-transfer-report', { params });
  return response.data;
};

export const exportInternalTransferReportCsv = async (params = {}) => {
  const response = await apiClient.get('/greige-storage/internal-transfer-report', {
    params: { ...params, format: 'csv' }, responseType: 'blob', timeout: 600000,
  });
  return response.data;
};

// Full detail (enriched bundles[]) for one internal transfer — used by the
// report's per-row "Transfer Sheet" / "Bundle-Wise" prints.
export const getInternalTransferDetail = async (transferId) => {
  const response = await apiClient.get(`/greige-storage/internal-transfer-detail/${transferId}`);
  return response.data;
};

// Assortment design-conversion report (source sort → target sort, per pick slip).
export const getAssortmentConversionReport = async (params = {}) => {
  const response = await apiClient.get('/greige-storage/assortment/conversion-report', { params });
  return response.data;
};

// Save an operator ERP entry number against a Greige-Storage internal transfer.
export const saveInternalTransferErpEntry = async (transferId, erpEntryNo) => {
  const response = await apiClient.post(
    `/greige-storage/internal-transfer/${transferId}/erp-entry`,
    { erp_entry_no: erpEntryNo });
  return response.data;
};

// Bundles picked on a completed assortment slip but not yet internally
// transferred out of Greige Storage (awaiting shipment). One row per bundle.
export const getPickedNotTransferredReport = async (params = {}) => {
  const response = await apiClient.get('/greige-storage/picked-not-transferred-report', { params });
  return response.data;
};

// Return a leftover picked (not-yet-transferred) bundle back to a storage bin.
export const returnPickedBundleToLocation = async (bundle_code, location_code, reason = '') => {
  const response = await apiClient.post('/greige-storage/picked-not-transferred/return-to-location',
    { bundle_code, location_code, reason });
  return response.data;
};
